import { store } from 'store';
import { MarketplacePetSize, MarketplaceServiceCategory } from 'types/Marketplace';
import { Pet, PetType, PetTypeIds } from 'types/Pet';
import { Service, ServiceDuration } from 'types/Service';
import {
    isSessionBusinessArea,
    OnlineBookingEvaluationItem,
    OnlineBookingItem,
    OnlineBookingPreferences,
    OnlineBookingPricingOption,
    OnlineBookingPricingOptions
} from 'types/OnlineBooking';
import { resolveWalkingServiceConfig, walkingMetadataHasDurationKeys } from 'types/Walking';

import {
    isDaycareServiceAvailableForPets as isDaycareAvailableForPets,
    getDaycareServiceRoomUnitAssignment as getDaycareRoomUnitAssignment,
    calculateDaycareHours as calcDaycareHours
} from 'utils/daycareUtils';
import {
    isBoardingServiceAvailableForPet as isBoardingAvailable,
    isBoardingServiceAvailableForPets as isBoardingAvailableForPets
} from 'utils/boardingUtils';
import { getRoomUnitAssignmentsForPets as getRoomUnitAssignments } from 'utils/room/roomUnitAssignment';

// Re-exports for backward compatibility
export type { DaycareServiceRoomUnitAssignment } from 'utils/daycareUtils';
export const getRoomUnitAssignmentsForPets = getRoomUnitAssignments;
export const getDaycareServiceRoomUnitAssignment = getDaycareRoomUnitAssignment;
export const isDaycareServiceAvailableForPets = isDaycareAvailableForPets;
export const isBoardingServiceAvailableForPets = isBoardingAvailableForPets;
export const calculateDaycareHours = calcDaycareHours;

export const getEffectiveServiceCategory = (
    item: OnlineBookingItem
): MarketplaceServiceCategory => {
    if (item.businessArea === MarketplaceServiceCategory.EVALUATION) {
        const evaluationCategory = (item as OnlineBookingEvaluationItem).evaluationBusinessArea;
        return evaluationCategory === MarketplaceServiceCategory.DENTAL
            ? MarketplaceServiceCategory.GROOMING
            : evaluationCategory;
    }

    if (item.businessArea === MarketplaceServiceCategory.DENTAL) {
        return MarketplaceServiceCategory.GROOMING;
    }

    return item.businessArea;
};

export const bookingRequiresApproval = (
    bookingItems: Array<OnlineBookingItem>,
    preferences: OnlineBookingPreferences
): boolean =>
    bookingItems.some(
        item => preferences.bookingRequestCategories[getEffectiveServiceCategory(item)] === true
    );

export const getAvailableServicesForPet = (
    services: Array<Service>,
    pet: Pet,
    serviceCategory: MarketplaceServiceCategory,
    nightsCount?: number
): Array<{
    service: Service;
    price: number;
}> => {
    const serviceCategories = [serviceCategory];

    if (serviceCategory === MarketplaceServiceCategory.GROOMING) {
        serviceCategories.push(MarketplaceServiceCategory.DENTAL);
    }

    return services
        .filter(service => serviceCategories.includes(service.businessArea))
        .filter(service =>
            isServiceAvailableForPet(service, pet, {
                nightsCount,
                businessArea: serviceCategory
            })
        )
        .map(service => ({
            service,
            price: getServicePriceByPet(service, pet, nightsCount)
        }))
        .sort((a, b) => a.service.name.localeCompare(b.service.name));
};

export const isServiceAvailableForPet = (
    service: Service,
    pet: Pet,
    options?: { nightsCount?: number; businessArea?: MarketplaceServiceCategory }
) => {
    const marketplace = store.getState().marketplace.data;

    // Marketplace has pet type active
    if (pet.type.id === PetTypeIds.CAT && !marketplace.catsEnabled) return false;
    if (pet.type.id === PetTypeIds.DOG && !marketplace.dogsEnabled) return false;
    if (pet.type.id === PetTypeIds.EXOTIC && !marketplace.exoticsEnabled) return false;

    if (
        [MarketplaceServiceCategory.GROOMING, MarketplaceServiceCategory.DENTAL].includes(
            service.businessArea
        )
    ) {
        return isGroomingServiceAvailableForPet(service, pet);
    }

    if (service.businessArea === MarketplaceServiceCategory.DAYCARE) {
        return isDaycareAvailableForPets(service, [pet]);
    }

    if (service.businessArea === MarketplaceServiceCategory.BOARDING) {
        return isBoardingAvailable(service, pet, options?.nightsCount);
    }

    if (service.businessArea === MarketplaceServiceCategory.EVALUATION) {
        return options?.businessArea === MarketplaceServiceCategory.EVALUATION;
    }

    return false;
};

const isGroomingServiceAvailableForPet = (service: Service, pet: Pet) => {
    if (service.fixedPrice.length > 0) {
        const hasValidFixedPrice = service.fixedPrice.some(fixedPrice => {
            return fixedPrice.petType.id === pet.type.id && fixedPrice.price != null;
        });

        // Has variable duration
        if (Array.isArray(service.variableDuration) && service.variableDuration.length > 0) {
            return hasValidFixedPrice && isVariableDurationServiceAvailableForPet(service, pet);
        }

        return hasValidFixedPrice;
    }

    if (service.variablePrice) {
        const hasValidVariablePrice = service.variablePrice.prices.some(variablePrice => {
            return (
                variablePrice.petType?.id === pet.type.id &&
                variablePrice.petSize?.id === pet.size.id &&
                variablePrice.price != null
            );
        });

        // Has variable duration
        if (Array.isArray(service.variableDuration) && service.variableDuration.length > 0) {
            return hasValidVariablePrice && isVariableDurationServiceAvailableForPet(service, pet);
        }

        return hasValidVariablePrice;
    }

    return false;
};

const isVariableDurationServiceAvailableForPet = (service: Service, pet: Pet) => {
    return (
        Array.isArray(service.variableDuration) &&
        service.variableDuration.some(
            variableDuration => variableDuration.petSize.id === pet.size.id
        )
    );
};

export const formatServicePriceLabel = (
    price: number,
    pricingOption: OnlineBookingPricingOption,
    suffix = ''
): string =>
    `${
        pricingOption === OnlineBookingPricingOptions.StartingAt ? 'Starting at ' : ''
    }$${price.toFixed(2)}${suffix}`;

export const getServicePriceByPet = (service: Service, pet: Pet, nightsCount?: number) => {
    if (service.businessArea === MarketplaceServiceCategory.EVALUATION) {
        return service.fixedPrice[0]?.price || 0;
    }

    if (
        [MarketplaceServiceCategory.GROOMING, MarketplaceServiceCategory.DENTAL].includes(
            service.businessArea
        )
    ) {
        return getGroomingServicePriceByPet(service, pet);
    }

    if (service.businessArea === MarketplaceServiceCategory.DAYCARE) {
        return getDaycareServicePriceByPet(service, pet);
    }

    if (service.businessArea === MarketplaceServiceCategory.BOARDING) {
        return getBoardingServicePriceByPet(service, pet, nightsCount);
    }

    if (isSessionBusinessArea(service.businessArea)) {
        return getDaycareServicePriceByPet(service, pet);
    }

    return 0;
};

const getGroomingServicePriceByPet = (service: Service, pet: Pet) => {
    let priceResult = 0;

    if (service.fixedPrice.length > 0) {
        priceResult = service.fixedPrice
            .filter(price => price.petType?.id === pet.type?.id)
            .map(p => p.price)
            .reduce((min, current) => Math.min(min || 0, current || 0), Infinity) as number;
    }

    if (service.variablePrice) {
        if (
            (service.variablePrice.variables.petSizeVariable &&
                service.variablePrice.variables.hairLengthVariable) ||
            service.variablePrice.variables.hairLengthVariable
        ) {
            priceResult = service.variablePrice.prices
                .filter(
                    price =>
                        price.petType?.id === pet.type?.id &&
                        price.petSize?.id === pet.size.id &&
                        (price.hairLength?.id === pet.hairLength?.id ||
                            pet.hairLength === undefined)
                )
                .map(p => p.price)
                .reduce((min, current) => Math.min(min || 0, current || 0), Infinity) as number;
        } else if (
            service.variablePrice.variables.petSizeVariable &&
            !service.variablePrice.variables.hairLengthVariable
        ) {
            priceResult = service.variablePrice.prices
                .filter(
                    price => price.petSize?.id === pet.size.id && price.petType?.id === pet.type?.id
                )
                .map(p => p.price)
                .reduce((min, current) => Math.min(min || 0, current || 0), Infinity) as number;
        }
    }

    if (!pet.type.id) {
        priceResult = 0;
    }

    return priceResult;
};

const getDaycareServicePriceByPet = (service: Service, pet: Pet) => {
    return service.fixedPrice.find(price => price.petType?.id === pet.type?.id)?.price || 0;
};

const getBoardingServicePriceByPet = (service: Service, pet: Pet, nightsCount?: number) => {
    const petTypePrice =
        service.fixedPrice.find(price => price.petType?.id === pet.type?.id)?.price || 0;

    if (nightsCount) {
        return petTypePrice * nightsCount;
    }

    return petTypePrice;
};

export const getServiceDurationByPet = (
    service: Service,
    petType: PetType,
    petSize: MarketplacePetSize
): number | false => {
    if (
        [MarketplaceServiceCategory.GROOMING, MarketplaceServiceCategory.DENTAL].includes(
            service.businessArea
        )
    ) {
        return getGroomingServiceDurationByPet(service, petType, petSize);
    }

    if (service.businessArea === MarketplaceServiceCategory.EVALUATION) {
        return getServiceDurationInMinutes({
            hours: service.durationHours || 0,
            minutes: service.durationMinutes || 0
        });
    }

    if (isSessionBusinessArea(service.businessArea)) {
        if (walkingMetadataHasDurationKeys(service)) {
            return resolveWalkingServiceConfig(service).durationMinutes;
        }

        return getServiceDurationInMinutes({
            hours: service.durationHours || 0,
            minutes: service.durationMinutes || 0
        });
    }

    return false;
};

const getGroomingServiceDurationByPet = (
    service: Service,
    petType: PetType,
    petSize: MarketplacePetSize
): number | false => {
    const marketplace = store.getState().marketplace.data;

    if (petType.id === PetTypeIds.CAT && !marketplace.catsEnabled) return false;
    if (petType.id === PetTypeIds.DOG && !marketplace.dogsEnabled) return false;
    if (petType.id === PetTypeIds.EXOTIC && !marketplace.exoticsEnabled) return false;

    let serviceDuration: ServiceDuration | undefined;

    if (Array.isArray(service.variableDuration) && service.variableDuration.length > 0) {
        const variableDurationForPet = service.variableDuration.find(
            duration => duration.petSize.id === petSize.id
        );

        if (!variableDurationForPet) {
            return false;
        }

        serviceDuration = variableDurationForPet.duration;
    }

    if (service.durationHours != null && service.durationMinutes != null) {
        serviceDuration = {
            hours: service.durationHours,
            minutes: service.durationMinutes
        };
    }

    if (!serviceDuration) return false;

    return getServiceDurationInMinutes(serviceDuration);
};

export const getServiceDurationInMinutes = (serviceDuration: ServiceDuration) => {
    const hoursInMinutes = serviceDuration.hours * 60;

    return hoursInMinutes + serviceDuration.minutes;
};
